import { PaintStrokeCommand, snapshotRegion } from "../commands/PaintStrokeCommand";
import { SetSelectionCommand } from "../commands/SetSelectionCommand";
import type { RasterLayer } from "../document/Layer";
import { findRaster } from "../document/LayerUtils";
import { Selection } from "../selection/Selection";
import { mergeBBox, stamp, stampLine, stampWithCeiling, stampLineWithCeiling, advancedStampLine, airbrushStamp, type BBox, type BrushMode } from "../pixel/brush";
import { applyStrokeSmoothing, defaultBrushSettings, type BrushSettings } from "../pixel/brushSettings";
import { LazyBboxSnapshot } from "../pixel/lazyBboxSnapshot";
import { screenToDoc } from "../render/Viewport";
import type { PointerSample, Tool, ToolContext } from "./Tool";

// Paints or erases on the active raster layer. Mutates the layer's pixel
// buffer live during the drag; at stroke end emits a PaintStrokeCommand
// carrying before/after patches for the touched bbox.
//
// Selection: when a doc selection exists, brush strokes are masked to the
// selection area (Photoshop behavior). Only pixels with selMask > 0 are painted.
//
// Quick Mask mode: when ctx.quickMask is true, brush strokes modify the
// document's selection mask instead of layer pixels. Painting with white
// (fg) adds to the selection; painting with black removes from it. The
// eraser in Q mode does the inverse.

export type { BrushSettings } from "../pixel/brushSettings";

/**
 * Photoshop Paint Symmetry (audit i77) — a reflection expressed in absolute
 * document space, mirroring around the doc's own center. ToolUX's Painting
 * Symmetry popover writes the chosen axis onto `tool.settings.symmetryAxis`
 * (a dynamic field — BrushSettings doesn't formally declare it, see
 * src/app/ui/ToolUX.ts). "both" (the popover's "Both" option — a dual
 * vertical+horizontal axis) yields all three extra quadrant copies.
 */
type SymmetryMirrorFn = (x: number, y: number, cx: number, cy: number) => { x: number; y: number };
const SYMMETRY_MIRROR_FNS: Record<string, SymmetryMirrorFn[]> = {
  vertical: [(x, y, cx) => ({ x: 2 * cx - x, y })],
  horizontal: [(x, y, _cx, cy) => ({ x, y: 2 * cy - y })],
  both: [
    (x, y, cx) => ({ x: 2 * cx - x, y }),
    (x, y, _cx, cy) => ({ x, y: 2 * cy - y }),
    (x, y, cx, cy) => ({ x: 2 * cx - x, y: 2 * cy - y }),
  ],
};

export class BrushTool implements Tool {
  readonly id: string;
  readonly shortcut: string;
  readonly mode: BrushMode;
  settings: BrushSettings = defaultBrushSettings();

  private active: {
    // Normal mode
    layer?: RasterLayer;
    /**
     * Lazy pre-stamp snapshotter. Only captures pixels in the regions the
     * brush has signalled it is about to touch (#78). For a tiny stroke on a
     * 4K layer this allocates <1 kB instead of 48 MB.
     */
    snapshotter?: LazyBboxSnapshot;
    /**
     * Per-pixel accumulated alpha for non-compounding stroke opacity (#218).
     * Tracks how much alpha has already been applied to each pixel in this
     * stroke. New stamps are clamped so that no pixel exceeds the stroke
     * Opacity ceiling. Allocated on first non-airbrush stamp; null = not yet
     * needed (pre-allocating for every stroke wastes memory on large layers).
     */
    strokeAlphaBuf?: Uint8Array | null;
    // Quick Mask mode
    maskBefore?: Uint8Array;
    // Shared
    lastX: number;
    lastY: number;
    bbox: BBox | null;
    /**
     * Layer-local doc coords where the current drag began (set once in
     * onPointerDown, normal-mode only). Used as the anchor for Shift+drag
     * 45°-axis constraint mid-stroke (audit finding 167) — same angle-snap
     * math as GradientTool. Undefined for quick-mask strokes, which don't
     * use this constraint.
     */
    strokeStartX?: number;
    strokeStartY?: number;
    // Airbrush tracking
    airbrushStart: number;
    airbrushLast: number;
    isHolding: boolean;
    // Stroke tracking for fade
    strokePosX: number;
    strokePosY: number;
    totalStrokeLen: number;
  } | null = null;

  private prevStrokeX: number | null = null;
  private prevStrokeY: number | null = null;
  private eyedropperActive = false;
  private savedFgColor: { r: number; g: number; b: number } | null = null;
  /**
   * Tracks whether a valid color sample was actually committed to the
   * foreground during the current Alt-eyedropper session (audit i130).
   * Photoshop keeps the sampled color as FG after Alt is released; it only
   * reverts to the pre-session color when Alt was released/clicked without
   * ever landing a valid sample (e.g. off-canvas or a fully transparent
   * pixel).
   */
  private fgSampledDuringEyedropper = false;

  constructor(opts: { id: string; shortcut: string; mode: BrushMode }) {
    this.id = opts.id;
    this.shortcut = opts.shortcut;
    this.mode = opts.mode;
  }

  // ── Host-keymap setters (#218) ──────────────────────────────────────
  static readonly HARDNESS_STEP = 0.25;
  adjustHardness(delta: number): void {
    this.settings.hardness = Math.max(0, Math.min(1, this.settings.hardness + delta));
  }
  setOpacityFromDigit(digit: number): void {
    this.settings.opacity = digit === 0 ? 1 : Math.max(0.1, Math.min(1, digit / 10));
  }
  setFlowFromDigit(digit: number): void {
    this.settings.flow = digit === 0 ? 1 : Math.max(0.1, Math.min(1, digit / 10));
  }
  private ensureStrokeAlphaBuf(layer: { pixels: { width: number; height: number } }): Uint8Array {
    if (!this.active) throw new Error('no active stroke');
    if (!this.active.strokeAlphaBuf) {
      this.active.strokeAlphaBuf = new Uint8Array(layer.pixels.width * layer.pixels.height);
    }
    return this.active.strokeAlphaBuf;
  }
  // ────────────────────────────────────────────────────────────────────

  /** Extract the selection mask from the tool context for stamp masking. */
  private getSelMask(ctx: ToolContext): { mask: Uint8Array | undefined; w: number | undefined } {
    const sel = (ctx.doc as any)?.selection as { mask?: Uint8Array } | null;
    if (!sel || !sel.mask) return { mask: undefined, w: undefined };
    return { mask: sel.mask, w: ctx.doc!.width };
  }

  /**
   * i165 — `findRaster` (LayerUtils.ts) also matches `kind === "background"`
   * layers and returns them typed as `RasterLayer` (whose `kind` field is
   * statically typed as the literal `"raster"`), so the runtime `kind` tag
   * is the only signal available here for telling the two apart. Takes a
   * structurally-widened `{ kind?: string }` param (rather than the narrower
   * `RasterLayer`) specifically so this runtime check compiles — comparing
   * a value statically typed as the literal `"raster"` against `"background"`
   * would otherwise be a TS2367 "no overlap" build error.
   */
  private isBackgroundLayer(layer: { kind?: string }): boolean {
    return layer.kind === "background";
  }

  /**
   * When erasing on a Background layer, Photoshop paints the current
   * background color instead of revealing transparency (a Background layer
   * has no alpha channel). Returns `undefined` for every other case
   * (non-erase mode, or a normal raster layer) so the erase stamps fall
   * through to their original alpha-reduction behavior byte-identical to
   * before this fix.
   */
  private eraseToColorFor(
    ctx: ToolContext,
    layer: { kind?: string },
  ): { r: number; g: number; b: number } | undefined {
    if (this.mode !== "erase") return undefined;
    if (!this.isBackgroundLayer(layer)) return undefined;
    return { r: ctx.bgColor.r, g: ctx.bgColor.g, b: ctx.bgColor.b };
  }

  /**
   * i77 — extra (mirrored) LAYER-LOCAL positions for a single dab, per Paint
   * Symmetry. Empty when `settings.symmetryAxis` is unset/"none" (the
   * default) or unrecognized, so a plain brush stroke is completely
   * unaffected — existing tests never set this field. The axis lines are
   * fixed to the document's own center (not the layer's), matching
   * Photoshop's on-canvas symmetry guide, which stays put regardless of
   * which layer is being painted on.
   */
  private symmetryMirrorPoints(
    ctx: ToolContext,
    layer: RasterLayer,
    docX: number,
    docY: number,
  ): Array<{ x: number; y: number }> {
    const axis = (this.settings as BrushSettings & { symmetryAxis?: string }).symmetryAxis;
    const fns = axis ? SYMMETRY_MIRROR_FNS[axis] : undefined;
    if (!fns || !ctx.doc) return [];
    const absX = docX + layer.x;
    const absY = docY + layer.y;
    const cx = ctx.doc.width / 2;
    const cy = ctx.doc.height / 2;
    return fns.map((fn) => {
      const p = fn(absX, absY, cx, cy);
      return { x: p.x - layer.x, y: p.y - layer.y };
    });
  }

  /**
   * Same idea as `symmetryMirrorPoints` but for a line SEGMENT — both
   * endpoints are reflected through the same transform so the mirrored copy
   * is a straight line too, not two independently-placed points.
   */
  private symmetryMirrorSegments(
    ctx: ToolContext,
    layer: RasterLayer,
    fromX: number,
    fromY: number,
    toX: number,
    toY: number,
  ): Array<{ fromX: number; fromY: number; toX: number; toY: number }> {
    const axis = (this.settings as BrushSettings & { symmetryAxis?: string }).symmetryAxis;
    const fns = axis ? SYMMETRY_MIRROR_FNS[axis] : undefined;
    if (!fns || !ctx.doc) return [];
    const cx = ctx.doc.width / 2;
    const cy = ctx.doc.height / 2;
    const absFromX = fromX + layer.x, absFromY = fromY + layer.y;
    const absToX = toX + layer.x, absToY = toY + layer.y;
    return fns.map((fn) => {
      const f = fn(absFromX, absFromY, cx, cy);
      const t = fn(absToX, absToY, cx, cy);
      return { fromX: f.x - layer.x, fromY: f.y - layer.y, toX: t.x - layer.x, toY: t.y - layer.y };
    });
  }

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc) return;

    if (s.altKey) {
      if (!this.eyedropperActive) {
        // Save current foreground color so we can restore it on Alt release
        // — but ONLY if this session never lands a valid sample (i130).
        this.savedFgColor = { r: ctx.fgColor.r, g: ctx.fgColor.g, b: ctx.fgColor.b };
        this.fgSampledDuringEyedropper = false;
        this.eyedropperActive = true;
      }
      this.sampleColor(ctx, s);
      return;
    }

    if (ctx.quickMask) {
      this.quickMaskPointerDown(ctx, s);
      return;
    }

    if (!ctx.doc.activeLayerId) return;
    const layer = findRaster(ctx.doc.layers, ctx.doc.activeLayerId);
    if (!layer || !layer.visible) return;
    // i165 (eraser half): a Background layer's `lockImage` flag marks "no
    // transparency yet" (see LayerUtils.ts / createBackgroundLayer), not a
    // real Photoshop "Lock Image Pixels" toggle — Photoshop lets you erase
    // directly on the Background layer (it paints the background color,
    // never introducing transparency), so the Eraser must not be blocked by
    // that flag here. Every other case — lockAll always, and lockImage on
    // any non-background layer or in paint mode — is unchanged.
    const bypassLockImage = this.mode === "erase" && this.isBackgroundLayer(layer);
    if (layer.lockAll || (layer.lockImage && !bypassLockImage)) {
      ctx.showTip("Layer is locked.");
      return;
    }
    const p = screenToDoc(ctx.viewport, s.x, s.y);

    // Shift+click: draw straight line from last stroke position
    if (s.shiftKey && this.prevStrokeX !== null && this.prevStrokeY !== null) {
      this.strokeLine(ctx, layer, this.prevStrokeX, this.prevStrokeY, p.x, p.y, s);
      this.prevStrokeX = p.x;
      this.prevStrokeY = p.y;
      layer.pixels.markDirty();
      ctx.requestRender();
      return;
    }

    const docX = p.x - layer.x;
    const docY = p.y - layer.y;
    // #78: defer pixel-buffer allocation. The lazy snapshotter captures only
    // the regions the brush touches.
    const snapshotter = new LazyBboxSnapshot(layer.pixels);
    this.active = {
      layer,
      snapshotter,
      strokeAlphaBuf: null,
      lastX: docX,
      lastY: docY,
      bbox: null,
      strokeStartX: docX,
      strokeStartY: docY,
      airbrushStart: s.timeStamp,
      airbrushLast: s.timeStamp,
      isHolding: false,
      strokePosX: 0,
      strokePosY: 0,
      totalStrokeLen: 0,
    };
    const { mask: selMask, w: selMaskW } = this.getSelMask(ctx);
    const lockAlpha = layer.lockTransparency ? extractAlpha(layer.pixels) : undefined;
    const lockAlphaW = lockAlpha ? layer.pixels.width : undefined;

    const effectiveRadius = (this.settings.size / 2) * this.sizePressureFactor(s);

    if (this.settings.airbrush) {
      this.active.isHolding = true;
      this.airbrushFrame(ctx, s);
    } else {
      // Pre-capture the bbox this stamp will touch.
      snapshotter.recordBeforeStamp(stampMaxBBox(docX, docY, effectiveRadius));
      const alphaBuf0 = this.ensureStrokeAlphaBuf(layer);
      const stampBase = {
        radius: effectiveRadius,
        hardness: this.settings.hardness,
        opacity: this.settings.flow * this.opacityPressureFactor(s),
        r: ctx.fgColor.r, g: ctx.fgColor.g, b: ctx.fgColor.b,
        mode: this.mode,
        selMask,
        selMaskW,
        lockAlpha,
        lockAlphaW,
        wetEdges: this.settings.wetEdges,
        blendMode: this.settings.blendMode,
        eraseToColor: this.eraseToColorFor(ctx, layer),
      };
      const bb = stampWithCeiling(layer.pixels, { cx: docX, cy: docY, ...stampBase }, alphaBuf0, this.settings.opacity);
      if (bb) this.active.bbox = bb;

      // i77: Paint Symmetry — mirror this dab across the configured axis.
      for (const m of this.symmetryMirrorPoints(ctx, layer, docX, docY)) {
        snapshotter.recordBeforeStamp(stampMaxBBox(m.x, m.y, effectiveRadius));
        const mbb = stampWithCeiling(layer.pixels, { cx: m.x, cy: m.y, ...stampBase }, alphaBuf0, this.settings.opacity);
        if (mbb) this.active.bbox = this.active.bbox ? mergeBBox(this.active.bbox, mbb) : mbb;
      }
    }
    layer.pixels.markDirty();
    ctx.requestRender();
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (this.eyedropperActive) {
      if (!s.altKey) { this.exitEyedropper(ctx); return; }
      this.sampleColor(ctx, s);
      return;
    }

    if (!this.active) return;

    if (ctx.quickMask) {
      this.quickMaskPointerMove(ctx, s);
      return;
    }

    const p = screenToDoc(ctx.viewport, s.x, s.y);
    let docX = p.x - this.active.layer!.x;
    let docY = p.y - this.active.layer!.y;

    // i76 — Brush options bar "Smoothing" slider: lag the raw pointer sample
    // toward the last stroke point before it becomes the next stroke point
    // (pure fn lives in pixel/brushSettings.ts; this was the missing wiring —
    // the tool computed docX/docY straight from the raw pointer and never
    // consulted settings.smoothing at all). Applied before the Shift 45° snap
    // below so a smoothed stroke still snaps cleanly to axis constraints.
    if (this.settings.smoothing && this.settings.smoothing > 0) {
      const smoothed = applyStrokeSmoothing(this.active.lastX, this.active.lastY, docX, docY, this.settings.smoothing);
      docX = smoothed.x;
      docY = smoothed.y;
    }

    // Shift+drag mid-stroke: constrain the current pointer position to the
    // nearest 45° increment measured from the stroke's start point, so the
    // whole drag paints a straight horizontal/vertical/diagonal line — same
    // angle-snap math as GradientTool (audit finding 167).
    if (s.shiftKey && this.active.strokeStartX !== undefined && this.active.strokeStartY !== undefined) {
      const anchorX = this.active.strokeStartX;
      const anchorY = this.active.strokeStartY;
      const dx = docX - anchorX;
      const dy = docY - anchorY;
      const angle = Math.atan2(dy, dx);
      const snapped = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
      const dist = Math.hypot(dx, dy);
      docX = anchorX + Math.cos(snapped) * dist;
      docY = anchorY + Math.sin(snapped) * dist;
    }

    if (this.settings.airbrush && this.active.isHolding) {
      const moveDist = Math.hypot(docX - this.active.lastX, docY - this.active.lastY);
      if (moveDist < 2) {
        this.airbrushFrame(ctx, s);
        return;
      }
      this.active.isHolding = false;
    }

    const { mask: selMask, w: selMaskW } = this.getSelMask(ctx);
    const lockAlpha = this.active.layer!.lockTransparency ? extractAlpha(this.active.layer!.pixels) : undefined;
    const lockAlphaW = lockAlpha ? this.active.layer!.pixels.width : undefined;

    const segLen = Math.hypot(docX - this.active.lastX, docY - this.active.lastY);
    this.active.totalStrokeLen += segLen;

    const pressure = this.resolvePressure(s);
    const effectiveRadius = (this.settings.size / 2) * this.sizePressureFactor(s);
    const opacityFactor = this.opacityPressureFactor(s);
    const effectiveOpacity = this.settings.wetEdges ? this.settings.opacity * opacityFactor : this.settings.flow * opacityFactor;

    const hasDynamics = this.settings.sizeJitter > 0
      || this.settings.angleJitter > 0
      || this.settings.scatter > 0
      || this.settings.opacityJitter > 0
      || this.settings.flowJitter > 0
      || this.settings.fgBgJitter > 0
      || this.settings.hueJitter > 0
      || this.settings.purity !== 0
      || this.settings.count > 1
      || this.settings.roundnessJitter > 0;

    // #78: record the segment bbox before stamping, and stamp the segment.
    // Factored into a closure so the i77 Paint Symmetry mirror loop below can
    // replay the exact same stamping logic against mirrored coordinates
    // without duplicating the dynamics/non-dynamics branching.
    const paintSegment = (fx: number, fy: number, tx: number, ty: number): BBox | null => {
      // #78: record the segment bbox before stamping. We use a worst-case
      // bbox that includes scatter for dynamics; this captures pixels for
      // all positions the brush may touch.
      this.active!.snapshotter?.recordBeforeStamp(
        lineSegmentBBox(fx, fy, tx, ty, effectiveRadius + (hasDynamics ? this.settings.scatter * this.settings.size : 0)),
      );

      if (hasDynamics) {
        return advancedStampLine({
          buf: this.active!.layer!.pixels,
          base: {
            radius: effectiveRadius,
            hardness: this.settings.hardness,
            opacity: effectiveOpacity,
            mode: this.mode,
            selMask,
            selMaskW,
            lockAlpha,
            lockAlphaW,
            wetEdges: this.settings.wetEdges,
            blendMode: this.settings.blendMode,
            eraseToColor: this.eraseToColorFor(ctx, this.active!.layer!),
          },
          fromX: fx,
          fromY: fy,
          toX: tx,
          toY: ty,
          settings: this.settings,
          fgR: ctx.fgColor.r, fgG: ctx.fgColor.g, fgB: ctx.fgColor.b,
          bgR: ctx.bgColor.r, bgG: ctx.bgColor.g, bgB: ctx.bgColor.b,
          pressure,
          strokePos: this.active!.totalStrokeLen,
          strokeLen: this.active!.totalStrokeLen + 1,
        });
      }
      const alphaBufM = this.ensureStrokeAlphaBuf(this.active!.layer!);
      return stampLineWithCeiling(
        this.active!.layer!.pixels,
        {
          radius: effectiveRadius,
          hardness: this.settings.hardness,
          opacity: effectiveOpacity,
          r: ctx.fgColor.r, g: ctx.fgColor.g, b: ctx.fgColor.b,
          mode: this.mode,
          selMask,
          selMaskW,
          lockAlpha,
          lockAlphaW,
          wetEdges: this.settings.wetEdges,
          blendMode: this.settings.blendMode,
          eraseToColor: this.eraseToColorFor(ctx, this.active!.layer!),
        },
        fx, fy, tx, ty,
        alphaBufM,
        this.settings.opacity,
        Math.max(0.5, this.settings.size * this.settings.spacing),
      );
    };

    let bb = paintSegment(this.active.lastX, this.active.lastY, docX, docY);

    // i77: Paint Symmetry — mirror this stroke segment across the configured axis.
    for (const seg of this.symmetryMirrorSegments(ctx, this.active.layer!, this.active.lastX, this.active.lastY, docX, docY)) {
      const mbb = paintSegment(seg.fromX, seg.fromY, seg.toX, seg.toY);
      if (mbb) bb = bb ? mergeBBox(bb, mbb) : mbb;
    }

    if (bb) this.active.bbox = this.active.bbox ? mergeBBox(this.active.bbox, bb) : bb;
    this.active.lastX = docX;
    this.active.lastY = docY;
    this.active.layer!.pixels.markDirty();
    ctx.requestRender();
  }

  onPointerUp(ctx: ToolContext, s?: PointerSample): void {
    if (this.eyedropperActive) {
      this.exitEyedropper(ctx);
      return;
    }

    if (!this.active) return;

    if (ctx.quickMask) {
      this.quickMaskPointerUp(ctx);
      return;
    }

    // i76 catch-up: the Smoothing lag filter (onPointerMove) leaves the painted
    // ink trailing behind the real pointer, so on release we paint one final
    // segment straight to the true raw release point with smoothing OFF — matching
    // Photoshop's smoothing "catch up" so a stroke always reaches where the
    // pointer/pen actually let go instead of stopping short. Only needed when
    // smoothing is active; when it's off the last onPointerMove already reached
    // the raw point, so behaviour is unchanged.
    if (s && this.active.layer && this.settings.smoothing && this.settings.smoothing > 0) {
      const saved = this.settings.smoothing;
      this.settings.smoothing = 0;
      try {
        this.onPointerMove(ctx, s);
      } finally {
        this.settings.smoothing = saved;
      }
      if (!this.active) return;
    }

    if (this.active.layer) {
      const layerX = this.active.lastX + this.active.layer.x;
      const layerY = this.active.lastY + this.active.layer.y;
      this.prevStrokeX = layerX;
      this.prevStrokeY = layerY;
    }

    if (!this.active.bbox) {
      this.active = null;
      return;
    }
    const { layer, snapshotter, bbox } = this.active;
    const after = snapshotRegion(layer!, bbox);
    // #78: extract the touched bbox from the lazy snapshot's cover region.
    // The cover region is guaranteed to fully contain `bbox` (we recorded
    // every stamp's planned bbox into the snapshotter, and the actual
    // touched bbox is a subset of those planned bboxes).
    const before = extractFromLazySnapshot(snapshotter!, bbox, layer!.pixels.width);
    this.active = null;
    ctx.emit(new PaintStrokeCommand(layer!.id, bbox, before, after));
  }

  onKeyUp(ctx: ToolContext, key: string): boolean | void {
    if (key === "Alt" && this.eyedropperActive) {
      this.exitEyedropper(ctx);
      return true;
    }
  }

  private exitEyedropper(ctx: ToolContext): void {
    this.eyedropperActive = false;
    // i130: only restore the pre-session color when no sample was actually
    // committed during this Alt session (e.g. Alt released without ever
    // landing a valid pixel, or every attempt was off-canvas / transparent).
    // If a color WAS sampled, Photoshop leaves it as the foreground color —
    // restoring here would silently discard the user's just-picked color.
    if (this.savedFgColor && !this.fgSampledDuringEyedropper) {
      ctx.setForegroundColor(this.savedFgColor);
    }
    this.savedFgColor = null;
    this.fgSampledDuringEyedropper = false;
  }

  /** Airbrush: stamp continuously at current position while holding. */
  private airbrushFrame(ctx: ToolContext, s: PointerSample): void {
    if (!this.active || !this.active.layer) return;
    const { mask: selMask, w: selMaskW } = this.getSelMask(ctx);
    const lockAlpha = this.active.layer.lockTransparency ? extractAlpha(this.active.layer.pixels) : undefined;
    const lockAlphaW = lockAlpha ? this.active.layer.pixels.width : undefined;

    const elapsedMs = s.timeStamp - this.active.airbrushStart;
    const deltaMs = s.timeStamp - this.active.airbrushLast;
    this.active.airbrushLast = s.timeStamp;

    const docX = this.active.lastX;
    const docY = this.active.lastY;
    const radius = this.settings.size / 2;

    // #78: capture pre-stamp pixels for the airbrush region.
    this.active.snapshotter?.recordBeforeStamp(
      stampMaxBBox(docX, docY, radius + this.settings.scatter * this.settings.size),
    );

    const airbrushBase = {
      radius,
      hardness: this.settings.hardness,
      opacity: this.settings.opacity,
      mode: this.mode,
      selMask,
      selMaskW,
      lockAlpha,
      lockAlphaW,
      blendMode: this.settings.blendMode,
      eraseToColor: this.eraseToColorFor(ctx, this.active.layer),
    };
    const bb = airbrushStamp({
      buf: this.active.layer.pixels,
      base: airbrushBase,
      cx: docX,
      cy: docY,
      settings: this.settings,
      fgR: ctx.fgColor.r, fgG: ctx.fgColor.g, fgB: ctx.fgColor.b,
      bgR: ctx.bgColor.r, bgG: ctx.bgColor.g, bgB: ctx.bgColor.b,
      elapsedMs,
      deltaMs,
      pressure: s.pressure,
    });

    if (bb) this.active.bbox = this.active.bbox ? mergeBBox(this.active.bbox, bb) : bb;

    // i77: Paint Symmetry — mirror the airbrush dab(s) across the configured axis.
    for (const m of this.symmetryMirrorPoints(ctx, this.active.layer, docX, docY)) {
      this.active.snapshotter?.recordBeforeStamp(
        stampMaxBBox(m.x, m.y, radius + this.settings.scatter * this.settings.size),
      );
      const mbb = airbrushStamp({
        buf: this.active.layer.pixels,
        base: airbrushBase,
        cx: m.x,
        cy: m.y,
        settings: this.settings,
        fgR: ctx.fgColor.r, fgG: ctx.fgColor.g, fgB: ctx.fgColor.b,
        bgR: ctx.bgColor.r, bgG: ctx.bgColor.g, bgB: ctx.bgColor.b,
        elapsedMs,
        deltaMs,
        pressure: s.pressure,
      });
      if (mbb) this.active.bbox = this.active.bbox ? mergeBBox(this.active.bbox, mbb) : mbb;
    }

    this.active.layer.pixels.markDirty();
    ctx.requestRender();
  }

  /** Resolve pressure from the sample, mapping [0,1] to a useful range. */
  private resolvePressure(s: PointerSample): number {
    const p = s.pressure > 0 ? s.pressure : 0.5;
    return Math.max(0.1, Math.min(1, p));
  }

  /**
   * Pressure factor applied to brush SIZE. Photoshop scales size by pen pressure
   * ONLY when Shape Dynamics → Size Control is "Pen Pressure". A plain mouse
   * stroke must be full size. The previous code multiplied resolvePressure()
   * (0.5 for a mouse) into the radius unconditionally, halving EVERY stroke
   * regardless of the dynamics settings (audit i7). Defaults to 1 (no scaling).
   */
  private sizePressureFactor(s: PointerSample): number {
    return this.settings.sizeJitterControl === "pen-pressure" ? this.resolvePressure(s) : 1;
  }

  /**
   * Pressure factor applied to per-dab opacity/flow. Governed by the Opacity
   * control when Wet Edges is on, else the Flow control; only "pen-pressure"
   * scales it. Defaults to 1 for mouse/off — matching Photoshop (audit i7).
   */
  private opacityPressureFactor(s: PointerSample): number {
    const control = this.settings.wetEdges ? this.settings.opacityJitterControl : this.settings.flowJitterControl;
    return control === "pen-pressure" ? this.resolvePressure(s) : 1;
  }

  /** Draw a straight line stroke between two doc-space points. */
  private strokeLine(
    ctx: ToolContext,
    layer: RasterLayer,
    fromX: number,
    fromY: number,
    toX: number,
    toY: number,
    s: PointerSample,
  ): void {
    const docFromX = fromX - layer.x;
    const docFromY = fromY - layer.y;
    const docToX = toX - layer.x;
    const docToY = toY - layer.y;

    // #78: lazy bbox snapshot. Allocates only for the segment region.
    const snapshotter = new LazyBboxSnapshot(layer.pixels);
    const { mask: selMask, w: selMaskW } = this.getSelMask(ctx);
    const lockAlpha = layer.lockTransparency ? extractAlpha(layer.pixels) : undefined;
    const lockAlphaW = lockAlpha ? layer.pixels.width : undefined;

    const pressure = this.resolvePressure(s);
    const effectiveRadius = (this.settings.size / 2) * this.sizePressureFactor(s);
    const opacityFactor = this.opacityPressureFactor(s);
    const effectiveOpacity = this.settings.wetEdges ? this.settings.opacity * opacityFactor : this.settings.flow * opacityFactor;

    const hasDynamics = this.settings.sizeJitter > 0
      || this.settings.angleJitter > 0
      || this.settings.scatter > 0
      || this.settings.opacityJitter > 0
      || this.settings.flowJitter > 0
      || this.settings.fgBgJitter > 0
      || this.settings.hueJitter > 0
      || this.settings.purity !== 0
      || this.settings.count > 1
      || this.settings.roundnessJitter > 0;

    // Factored into a closure so the i77 Paint Symmetry mirror loop below can
    // replay the exact same stamping logic against mirrored coordinates
    // without duplicating the dynamics/non-dynamics branching.
    const paintSegment = (fx: number, fy: number, tx: number, ty: number): BBox | null => {
      snapshotter.recordBeforeStamp(
        lineSegmentBBox(fx, fy, tx, ty, effectiveRadius + (hasDynamics ? this.settings.scatter * this.settings.size : 0)),
      );

      if (hasDynamics) {
        const segLen = Math.hypot(tx - fx, ty - fy);
        return advancedStampLine({
          buf: layer.pixels,
          base: {
            radius: effectiveRadius,
            hardness: this.settings.hardness,
            opacity: effectiveOpacity,
            mode: this.mode,
            selMask,
            selMaskW,
            lockAlpha,
            lockAlphaW,
            wetEdges: this.settings.wetEdges,
            blendMode: this.settings.blendMode,
            eraseToColor: this.eraseToColorFor(ctx, layer),
          },
          fromX: fx,
          fromY: fy,
          toX: tx,
          toY: ty,
          settings: this.settings,
          fgR: ctx.fgColor.r, fgG: ctx.fgColor.g, fgB: ctx.fgColor.b,
          bgR: ctx.bgColor.r, bgG: ctx.bgColor.g, bgB: ctx.bgColor.b,
          pressure,
          strokePos: segLen,
          strokeLen: segLen + 1,
        });
      }
      return stampLine(
        layer.pixels,
        {
          radius: effectiveRadius,
          hardness: this.settings.hardness,
          opacity: effectiveOpacity,
          r: ctx.fgColor.r, g: ctx.fgColor.g, b: ctx.fgColor.b,
          mode: this.mode,
          selMask,
          selMaskW,
          lockAlpha,
          lockAlphaW,
          wetEdges: this.settings.wetEdges,
          blendMode: this.settings.blendMode,
          eraseToColor: this.eraseToColorFor(ctx, layer),
        },
        fx, fy, tx, ty,
        Math.max(0.5, this.settings.size * this.settings.spacing),
      );
    };

    let bb = paintSegment(docFromX, docFromY, docToX, docToY);

    // i77: Paint Symmetry — mirror this straight-line stroke across the configured axis.
    for (const seg of this.symmetryMirrorSegments(ctx, layer, docFromX, docFromY, docToX, docToY)) {
      const mbb = paintSegment(seg.fromX, seg.fromY, seg.toX, seg.toY);
      if (mbb) bb = bb ? mergeBBox(bb, mbb) : mbb;
    }

    if (bb) {
      const after = snapshotRegion(layer, bb);
      const before = extractFromLazySnapshot(snapshotter, bb, layer.pixels.width);
      ctx.emit(new PaintStrokeCommand(layer.id, bb, before, after));
    }
  }

  /** Sample color under cursor (Alt+click eyedropper while brush is active). */
  private sampleColor(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const layerId = ctx.doc.activeLayerId;
    if (!layerId) return;
    const layer = findRaster(ctx.doc.layers, layerId);
    if (!layer || !layer.visible) return;
    const px = Math.floor(p.x - layer.x);
    const py = Math.floor(p.y - layer.y);
    if (px < 0 || py < 0 || px >= layer.pixels.width || py >= layer.pixels.height) return;
    const i = (py * layer.pixels.width + px) * 4;
    const a = layer.pixels.data[i + 3] ?? 0;
    if (a === 0) return;
    const color = {
      r: layer.pixels.data[i] ?? 0,
      g: layer.pixels.data[i + 1] ?? 0,
      b: layer.pixels.data[i + 2] ?? 0,
    };
    if (s.shiftKey) {
      ctx.setBackgroundColor(color);
    } else {
      ctx.setForegroundColor(color);
      // i130: a real FG sample landed this session — exitEyedropper must
      // not clobber it with the pre-session saved color.
      this.fgSampledDuringEyedropper = true;
    }
  }

  // =================== Quick Mask helpers ===================

  private quickMaskPointerDown(ctx: ToolContext, s: PointerSample): void {
    const doc = ctx.doc!;
    const sel = doc.selection ?? Selection.empty(doc.width, doc.height);

    // Clone the mask so we can detect changes.
    const maskClone = new Uint8Array(sel.mask);
    this.active = {
      lastX: 0,
      lastY: 0,
      bbox: null,
      maskBefore: maskClone,
      airbrushStart: 0,
      airbrushLast: 0,
      isHolding: false,
      strokePosX: 0,
      strokePosY: 0,
      totalStrokeLen: 0,
    };

    const p = screenToDoc(ctx.viewport, s.x, s.y);
    this.quickMaskStamp(ctx, sel, p.x, p.y, this.active!);
    this.active!.lastX = p.x;
    this.active!.lastY = p.y;

    // Replace the doc's selection with the modified one (transient).
    ctx.emitTransient(new SetSelectionCommand(sel));
  }

  private quickMaskPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!this.active || !ctx.doc) return;
    const doc = ctx.doc;
    // The current selection comes from the doc (updated by transients).
    const sel = doc.selection ?? Selection.empty(doc.width, doc.height);
    const p = screenToDoc(ctx.viewport, s.x, s.y);

    // Stamp along the line from last pos to current pos.
    const step = Math.max(0.5, this.settings.size / 2 * 0.1);
    const dx = p.x - this.active.lastX;
    const dy = p.y - this.active.lastY;
    const len = Math.hypot(dx, dy);
    const n = Math.max(1, Math.ceil(len / step));

    for (let i = 1; i <= n; i++) {
      const t = i / n;
      const cx = this.active.lastX + dx * t;
      const cy = this.active.lastY + dy * t;
      this.quickMaskStamp(ctx, sel, cx, cy, this.active);
    }

    this.active.lastX = p.x;
    this.active.lastY = p.y;
    ctx.emitTransient(new SetSelectionCommand(sel));
  }

  private quickMaskPointerUp(ctx: ToolContext): void {
    const doc = ctx.doc;
    const sel = doc?.selection;
    if (!sel) {
      this.active = null;
      return;
    }

    // Emit a final undoable command. The invert will capture the selection
    // from before this emit (which is the state after the last transient,
    // i.e. the painted selection). To get a proper undo back to pre-drag
    // state, we create a special two-step: first emit a transient that
    // restores the original, then emit the final... but that's complex.
    //
    // For MVP: emit SetSelectionCommand. The user can Ctrl+Z to undo the
    // entire quick mask paint stroke; a second Ctrl+Z goes back to the
    // pre-Q-mode state (since entering Q mode doesn't create an undo entry).
    this.active = null;
    ctx.emit(new SetSelectionCommand(sel));
  }

  /**
   * Stamp on the selection mask: paints toward a mask level derived
   * continuously from the foreground color's luminance — white → fully
   * selected (255), black → fully unselected (0), and any gray in between
   * maps proportionally (audit finding 169; previously this snapped to a
   * hard 0/255 at the isLight > 128 threshold, so e.g. a mid-gray FG behaved
   * identically to pure white). The eraser inverts the target level (it
   * still respects gray, just pushes toward the opposite end). This matches
   * Photoshop's Q-mode behavior: white = add to selection, black = remove,
   * gray = partial, in every case scaled by the brush Opacity.
   */
  private quickMaskStamp(
    ctx: ToolContext,
    sel: Selection,
    cx: number,
    cy: number,
    _active: NonNullable<typeof BrushTool.prototype.active>,
  ): void {
    const radius = this.settings.size / 2;
    const r2 = radius * radius;
    const minX = Math.max(0, Math.floor(cx - radius));
    const minY = Math.max(0, Math.floor(cy - radius));
    const maxX = Math.min(sel.docW, Math.ceil(cx + radius));
    const maxY = Math.min(sel.docH, Math.ceil(cy + radius));

    // Continuous target mask level implied by the FG color's luminance:
    // 0 (black) → 0, 255 (white) → 255, gray → proportional in between.
    // Eraser mode inverts the direction (still continuous, just flipped).
    const lum = ctx.fgColor.r * 0.3 + ctx.fgColor.g * 0.59 + ctx.fgColor.b * 0.11; // 0..255
    const targetLevel = this.mode === "erase" ? 255 - lum : lum;
    const opacity = this.settings.opacity; // 0..1 — how strongly this stamp pushes toward targetLevel

    const mask = sel.mask;
    const w = sel.docW;
    let touchedX0 = w, touchedY0 = sel.docH, touchedX1 = -1, touchedY1 = -1;

    for (let py = minY; py < maxY; py++) {
      const dy = py + 0.5 - cy;
      const dy2 = dy * dy;
      for (let px = minX; px < maxX; px++) {
        const dx = px + 0.5 - cx;
        if (dx * dx + dy2 >= r2) continue;
        const idx = py * w + px;
        const cur = mask[idx] ?? 0;
        // Blend the current mask value toward targetLevel by `opacity` —
        // same idea as brush opacity on a normal layer: repeated strokes
        // accumulate toward (but never overshoot) the target level, and
        // partial opacity now applies symmetrically whether the stroke is
        // adding to or removing from the selection.
        mask[idx] = cur + (targetLevel - cur) * opacity;
        if (px < touchedX0) touchedX0 = px;
        if (py < touchedY0) touchedY0 = py;
        if (px > touchedX1) touchedX1 = px;
        if (py > touchedY1) touchedY1 = py;
      }
    }

    if (touchedX1 >= 0) {
      const bb: BBox = {
        x: touchedX0, y: touchedY0,
        w: touchedX1 - touchedX0 + 1,
        h: touchedY1 - touchedY0 + 1,
      };
      _active.bbox = _active.bbox ? mergeBBox(_active.bbox, bb) : bb;
    }
  }
}

function extractRegion(full: Uint8ClampedArray, fullWidth: number, bbox: BBox): Uint8ClampedArray {
  const out = new Uint8ClampedArray(bbox.w * bbox.h * 4);
  for (let row = 0; row < bbox.h; row++) {
    const srcOff = ((bbox.y + row) * fullWidth + bbox.x) * 4;
    const dstOff = row * bbox.w * 4;
    out.set(full.subarray(srcOff, srcOff + bbox.w * 4), dstOff);
  }
  return out;
}

/** Maximum bbox a single round stamp can touch. */
function stampMaxBBox(cx: number, cy: number, radius: number): BBox {
  const r = Math.max(0.5, radius);
  const x0 = Math.floor(cx - r);
  const y0 = Math.floor(cy - r);
  const x1 = Math.ceil(cx + r);
  const y1 = Math.ceil(cy + r);
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/** Maximum bbox a stamped line segment can touch. */
function lineSegmentBBox(
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  radius: number,
): BBox {
  const r = Math.max(0.5, radius);
  const x0 = Math.floor(Math.min(fromX, toX) - r);
  const y0 = Math.floor(Math.min(fromY, toY) - r);
  const x1 = Math.ceil(Math.max(fromX, toX) + r);
  const y1 = Math.ceil(Math.max(fromY, toY) + r);
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/**
 * Extract a sub-region (`bbox`) from the lazy snapshot's stored cover area.
 * Falls back to layer-buffer read for any region not actually covered (should
 * not happen if the caller recorded all stamp bboxes correctly, but defensive
 * code keeps undo correct even if a stamp slipped through).
 */
function extractFromLazySnapshot(
  snap: LazyBboxSnapshot,
  bbox: BBox,
  layerWidth: number,
): Uint8ClampedArray {
  const cover = snap.bbox;
  const pixels = snap.pixels;
  if (!cover || !pixels) {
    // Should not occur — bbox is non-null implies at least one stamp happened.
    return new Uint8ClampedArray(bbox.w * bbox.h * 4);
  }
  const out = new Uint8ClampedArray(bbox.w * bbox.h * 4);
  for (let row = 0; row < bbox.h; row++) {
    const yInCover = bbox.y + row - cover.y;
    if (yInCover < 0 || yInCover >= cover.h) {
      // Row outside cover — zero-fill (defensive).
      continue;
    }
    for (let col = 0; col < bbox.w; col++) {
      const xInCover = bbox.x + col - cover.x;
      if (xInCover < 0 || xInCover >= cover.w) continue;
      const srcOff = (yInCover * cover.w + xInCover) * 4;
      const dstOff = (row * bbox.w + col) * 4;
      out[dstOff] = pixels[srcOff]!;
      out[dstOff + 1] = pixels[srcOff + 1]!;
      out[dstOff + 2] = pixels[srcOff + 2]!;
      out[dstOff + 3] = pixels[srcOff + 3]!;
    }
  }
  void layerWidth;
  return out;
}

/** Extract per-pixel alpha from a PixelBuffer into a Uint8Array (0–255). */
function extractAlpha(buf: { data: Uint8ClampedArray; width: number }): Uint8Array {
  const len = buf.data.length;
  const out = new Uint8Array(len / 4);
  for (let i = 3, j = 0; i < len; i += 4, j++) {
    out[j] = buf.data[i]!;
  }
  return out;
}
